import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Button,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
} from '@mui/material';
import {
  PlayArrow,
  Close,
  Apple,
  Android,
  QrCodeScanner,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface App {
  id: number;
  title: string;
  image: string;
  description: string;
  longDescription: string;
  category: string[];
  platform: string[];
  releaseDate: string;
  status: 'released' | 'in-development' | 'upcoming';
  stats?: {
    downloads?: string;
    rating?: number;
    users?: string;
  };
  features?: string[];
}

const apps: App[] = [
  {
    id: 1,
    title: 'Expense Tracker',
    image: '/Icons/Coming Soon.svg',
    description: 'A simple and smart expense tracking app to manage daily spending, budgets, and savings goals.',
    longDescription: 'Expense Tracker helps you record income and expenses, organize transactions by category, and monitor your monthly budget with ease. With clean charts, quick entry, and smart summaries, it keeps your finances clear and under control.',
    category: ['Utility', 'Productivity', 'Tools'],
    platform: ['Android', 'iOS'],
    releaseDate: 'Released - 2025',
    status: 'released',
    stats: {
      downloads: 'Available on Play Store & App Store',
      rating: 0,
      users: 'Just Launched',
    },
    features: [
      'Quick income and expense entry',
      'Category-based transaction management',
      'Monthly and weekly spending summaries',
      'Budget planning and tracking',
      'Clean and intuitive dashboard',
      'Secure local data handling',
    ],
  },
];

const Apps = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);

  const filteredApps = apps.filter(app => {
    if (selectedTab === 0) return true;
    if (selectedTab === 1) return app.status === 'released';
    if (selectedTab === 2) return app.status === 'in-development';
    if (selectedTab === 3) return app.status === 'upcoming';
    return true;
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const getPlatformIcon = (platform: string) => {
    switch(platform.toLowerCase()) {
      case 'android': return <Android sx={{ fontSize: 16 }} />;
      case 'ios': return <Apple sx={{ fontSize: 16 }} />;
      default: return null;
    }
  };

  return (
    <Box sx={{ py: 8, minHeight: '100vh' }}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ fontWeight: 'bold', mb: 2 }}
          >
            Our Apps Portfolio
          </Typography>
          <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 6 }}>
            Mobile apps built with Flutter, designed for utility and productivity on Android & iOS
          </Typography>

          {/* Filter Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 6 }}>
            <Tabs 
              value={selectedTab} 
              onChange={handleTabChange} 
              centered
              sx={{
                '& .MuiTab-root': {
                  fontSize: '1rem',
                  fontWeight: 600,
                  px: 4,
                },
              }}
            >
              <Tab label="All Apps" />
              <Tab label="Released" />
              <Tab label="In Development" />
              <Tab label="Upcoming" />
            </Tabs>
          </Box>

          {/* Apps Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 4 }}>
            {filteredApps.length === 0 ? (
              <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 8 }}>
                <QrCodeScanner sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h5" color="text.secondary">
                  No apps in this category yet
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  Check back soon for new releases!
                </Typography>
              </Box>
            ) : (
              filteredApps.map((app, index) => (
                <motion.div
                  key={app.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor: 'background.paper',
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'all 0.3s',
                      border: '1px solid transparent',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        borderColor: 'primary.main',
                        boxShadow: '0 12px 24px rgba(229,231,235,0.25)',
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={app.image}
                        alt={app.title}
                        sx={{ objectFit: 'cover' }}
                      />
                      <Chip
                        label={app.status.replace('-', ' ').toUpperCase()}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          bgcolor: app.status === 'released' ? '#4CAF50' : 
                                   app.status === 'in-development' ? '#FF9800' : '#2196F3',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      />
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {app.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {app.description}
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                        {app.category.slice(0, 3).map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(229,231,235,0.15)',
                              color: 'primary.main',
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Stack>

                      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                        {app.platform.slice(0, 3).map((platform) => (
                          <Chip
                            key={platform}
                            icon={getPlatformIcon(platform) || undefined}
                            label={platform}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                          />
                        ))}
                      </Stack>

                      {app.stats && (
                        <Stack spacing={1} sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Downloads: <strong>{app.stats.downloads}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Rating: <strong>{app.stats.rating}/5.0</strong> ⭐
                          </Typography>
                        </Stack>
                      )}

                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        {app.releaseDate}
                      </Typography>

                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<PlayArrow />}
                        onClick={() => setSelectedApp(app)}
                        sx={{
                          background: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)',
                          color: '#FFFFFF',
                          fontWeight: 600,
                          borderRadius: '12px',
                          py: 1.5,
                          boxShadow: '0 4px 14px rgba(55, 65, 81, 0.25)',
                          transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
                            transform: 'translateY(-3px)',
                            boxShadow: '0 8px 25px rgba(55, 65, 81, 0.35)',
                          },
                          '&:active': {
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        Learn More
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </Box>
        </motion.div>
      </Container>

      {/* App Detail Dialog */}
      <Dialog
        open={Boolean(selectedApp)}
        onClose={() => setSelectedApp(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedApp && (
          <DialogContent sx={{ p: 0, bgcolor: 'background.paper' }}>
            <IconButton
              onClick={() => setSelectedApp(null)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                zIndex: 1,
              }}
            >
              <Close />
            </IconButton>
            <CardMedia
              component="img"
              height="300"
              image={selectedApp.image}
              alt={selectedApp.title}
            />
            <Box sx={{ p: 4 }}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                {selectedApp.title}
              </Typography>
              
              <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                {selectedApp.category.map((tag) => (
                  <Chip key={tag} label={tag} color="primary" />
                ))}
              </Stack>

              <Typography variant="body1" paragraph>
                {selectedApp.longDescription}
              </Typography>

              {selectedApp.features && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Key Features:
                  </Typography>
                  <Stack spacing={1}>
                    {selectedApp.features.map((feature, idx) => (
                      <Typography key={idx} variant="body2">
                        • {feature}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}

              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Available on:
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                {selectedApp.platform.map((platform) => (
                  <Chip
                    key={platform}
                    icon={getPlatformIcon(platform) || undefined}
                    label={platform}
                    variant="outlined"
                  />
                ))}
              </Stack>

              {selectedApp.stats && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                      {selectedApp.stats.downloads}
                    </Typography>
                    <Typography variant="caption">Downloads</Typography>
                  </Paper>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                      {selectedApp.stats.rating}
                    </Typography>
                    <Typography variant="caption">Rating</Typography>
                  </Paper>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                      {selectedApp.stats.users}
                    </Typography>
                    <Typography variant="caption">Active Users</Typography>
                  </Paper>
                </Box>
              )}

              <Typography variant="body2" color="text.secondary">
                {selectedApp.releaseDate}
              </Typography>
            </Box>
          </DialogContent>
        )}
      </Dialog>
    </Box>
  );
};

export default Apps;
